<template>
  <div>
    <!-- This modal opens when clicking on the eye (visibility) icon -->
    <NrModal
      :show="isChangeVisiModalActive"
      class="visibility-form"
      title="Change document visibility"
      :primaryButtonText="setDocVisibilityConfirmText"
      :disableButtons="disableSubmitVisibilityBtn"
      hasSecondaryButton
      secondaryButtonText="Cancel"
      @on-primary-click="updateDocVisibility"
      @on-secondary-click="clearVisibilityModalFields"
      @on-close-click="clearVisibilityModalFields"
    >
      <p>
        You are about to set the visibility of
        {{ selectAll ? 'all' : documentsSelectedCount }} document(s). Do you
        want to continue?
      </p>
      <RiskReport
        v-if="hasDocPiiFeature"
        :documents="selectedDocuments"
        :filter-params="documentFilterParams"
        :selectAll="selectAll"
      />
      <NrSelect
        v-model="selectedDocumentVisibility"
        placeholder="Select visibility"
        :options="documentVisibilities"
        append-to-body
      >
        <template v-slot:label>
          <span> Document visibility </span>
        </template>
      </NrSelect>
      <div class="m-top-12" v-if="requesterOrPublicSelected">
        <NrFormLabel>Include message to requester (optional)</NrFormLabel>
        <NrSelect
          v-model="selectedMessageTemplates"
          :placeholder="
            selectedMessageTemplates.length ? '' : 'Search message templates'
          "
          :options="filteredMessageTemplates"
          :filterable="false"
          :loading="dataFetchState.message_templates.fetching"
          :hasMore="!dataFetchState.message_templates.end"
          @search="searchMessageTemplates"
          @open="searchMessageTemplates"
          @input="setMessageTemplate"
          @load-more="loadMoreMessageTemplates"
          append-to-body
          multiple
          hideLabel
        />
        <nr-editor
          v-model="changeVisibilityMessage"
          :rows="12"
          strike
          bulletList
          orderedList
          link
        />
        <NrFormInput
          type="checkbox"
          class="m-top-12"
          label="Yes, send to requester (required)"
          v-model="sendMessageToRequester"
        />
      </div>
    </NrModal>
    <!-- This modal opens when clicking on the folder icon -->
    <NrModal
      title="Move to folder"
      hasSecondaryButton
      :show="isMoveToFolderModalActive"
      secondaryButtonText="Cancel"
      primaryButtonText="Save"
      @on-primary-click="moveToFolder"
      @on-close-click="closeMoveToFolder"
      @on-secondary-click="closeMoveToFolder"
      :disable-buttons="!selectedFolder"
    >
      <form @submit.prevent.stop>
        <p>
          You are about to move
          {{ selectAll ? 'all' : documentsSelectedCount }} document(s). Do you
          want to continue?
        </p>
        <fieldset>
          <div class="field m-top-8">
            <NrSelect
              class="m-top-12"
              placeholder="Search and select a folder"
              :options="folders"
              @search="handleTypingFolderSearch"
              @option:created="handleFolderAdd"
              v-model="selectedFolder"
              append-to-body
              taggable
            >
              <template v-slot:label>Folder</template>
            </NrSelect>
          </div>
        </fieldset>
      </form>
    </NrModal>
    <!-- This modal opens when clicking on the trash icon  -->
    <NrModal
      class="doc-delete-modal"
      title="Delete documents"
      :show="isDeleteModalActive"
      :disableButtons="disableDeleteSubmitBtn"
      hasSecondaryButton
      primary-button-text="Delete"
      @on-primary-click="onDeleteSubmit"
      @on-secondary-click="setCurrentAction('')"
    >
      <p>
        You are about to delete
        {{ selectAll ? 'all' : documentsSelectedCount }} document(s). This
        action cannot be undone. Do you want to continue?
      </p>
    </NrModal>
    <NrModal
      :show="isDocEmptyModalActive"
      title="No documents selected"
      hasSecondaryButton
      secondaryButtonText="Cancel"
      primaryButtonText="Ok"
      @on-primary-click="setCurrentAction('')"
      @on-close-click="setCurrentAction('')"
      @on-secondary-click="setCurrentAction('')"
    >
      <p>Please select one or more documents to perform any action.</p>
    </NrModal>
    <!-- This modal opens when clicking on the pencil icon -->
    <NrModal
      title="Rename document"
      :show="isRenameDocModalActive"
      hasSecondaryButton
      primary-button-text="Save"
      :disableButtons="invalidDocTitle"
      @on-primary-click="onDocRenameSubmit"
      @on-secondary-click="setCurrentAction('')"
    >
      <NrFormInput
        label="Rename document"
        v-model="docTitle"
        :validations="{
          text: {
            message: invalidDocTitle ? 'Document title cannot be blank.' : '',
            validate: !invalidDocTitle.length
          }
        }"
      />
    </NrModal>
    <!-- This modal opens when clicking on the archive icon -->
    <NrModal
      :show="isChangeExpirationModalActive"
      class="expiration-form"
      title="Change document expiration status"
      primaryButtonText="Change retention status"
      :disableButtons="disableSubmitExpirationBtn"
      hasSecondaryButton
      secondaryButtonText="Cancel"
      @on-primary-click="updateDocExpirationStatus"
      @on-secondary-click="clearExpirationModalFields"
      @on-close-click="clearExpirationModalFields"
    >
      <NrSelect
        v-model="selectedDocumentExpirationStatus"
        placeholder="Select a retention status"
        :options="documentExpirationStatuses"
        append-to-body
      >
        <template v-slot:label>
          <span>
            Change document retention status
            <NrTooltip class="inline">
              <template v-slot:label>
                <span>
                  Exempt from expungement: If you exempt documents from they
                  will not be automatically deleted from the portal even after
                  the normal retention period passes. If the request is deleted,
                  deleted, documents can be still accessed from the documents
                  page.
                  <br />
                  <br />
                  Mark for deletion: Return document(s) to a state where they
                  will be deleted when their retention period is up. If
                  retention period is in the past, documents will be deleted
                  during the next retention cycle.
                </span>
              </template>
              <NrIcon class="inline-icon" name="info-circle" />
            </NrTooltip>
          </span>
        </template>
      </NrSelect>
      <p>
        You are about to change the retention status for
        {{ selectAll ? 'all' : documentsSelectedCount }} document(s).
      </p>
    </NrModal>
  </div>
</template>

<script>
import NrIcon from '@nextrequestco/components/src/components/NrIcon/NrIcon.vue'
import NrModal from '@nextrequestco/components/src/components/NrModal/nr-modal.vue'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip/NrTooltip.vue'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput/nr-form-input.vue'
import NrFormLabel from '@nextrequestco/components/src/components/form-elements/nr-form-label.vue'
import NrEditor from '@nextrequestco/components/src/components/NrEditor/nr-editor.vue'
import RiskReport from '../components/RiskReport'
import NrSelect from '@nextrequestco/components/src/components/NrSelect/nr-select.vue'
import { mapActions, mapState } from 'vuex'
import throttle from 'lodash/throttle'
import isObject from 'lodash/isObject'

export default {
  name: 'DocumentToolbar',
  components: {
    NrIcon,
    NrTooltip,
    NrModal,
    NrFormInput,
    RiskReport,
    'nr-editor': NrEditor,
    NrSelect,
    NrFormLabel
  },
  props: {
    prettyId: {
      type: String,
      required: true
    },
    activeDocTitle: {
      type: String
    },
    selectedDocIds: {
      type: Array
    },
    selectedDocuments: {
      type: Array
    },
    documentFilterParams: {
      type: Object
    },
    documentsSelectedCount: {
      type: Number
    },
    selectAll: {
      type: Boolean
    }
  },
  data() {
    return {
      selectedFolder: null,
      shouldAddFolder: true,
      searchFolderTerm: '',
      selectedMessageTemplates: [],
      docTitle: '',
      docFolder: '',
      sendMessageToRequester: false,
      messageTemplateSearchTerm: '',
      selectedDocumentExpirationStatus: null,
      documentVisibilities: [
        { id: 'staff', label: 'Staff only' },
        { id: 'requester', label: 'Requester + staff' },
        { id: 'public', label: 'Public' }
      ],
      documentExpirationStatuses: [
        { id: 'true', label: 'Exempt from expungement' },
        { id: 'false', label: 'Mark for deletion' }
      ]
    }
  },
  watch: {
    activeDocTitle(title) {
      this.docTitle = title
    }
  },
  computed: {
    ...mapState('request_show', [
      'selectedChangeDocVisibility',
      'messageToRequester',
      'documentsFolders',
      'request',
      'dataFetchState',
      'currentAction',
      'dataOptions',
      'currentDocumentFilters'
    ]),

    ...mapState({
      hasDocPiiFeature: (state) =>
        state.account && state.account.pii_scan_feature_flag
    }),

    filteredMessageTemplates() {
      return this.dataOptions.message_templates.map((temp) => ({
        value: temp.id,
        label: temp.message_template_name,
        description: temp.message_template_description
      }))
    },
    folders() {
      return [{ id: -1, folder_name: 'No folder' }, ...this.documentsFolders]
        .map((folder) => ({
          value: folder.id,
          label: folder.folder_name
        }))
        .filter((folder) => {
          if (
            this.currentDocumentFilters &&
            this.currentDocumentFilters.folderless_docs
          ) {
            return folder.value !== -1
          } else {
            return true
          }
        })
    },

    isChangeVisiModalActive() {
      return this.currentAction === 'changeVisibility'
    },
    isMoveToFolderModalActive() {
      return this.currentAction === 'moveToFolder'
    },
    isDeleteModalActive() {
      return this.currentAction === 'docDelete'
    },
    isRenameDocModalActive() {
      return this.currentAction === 'docRename'
    },
    isChangeExpirationModalActive() {
      return this.currentAction === 'changeExpiration'
    },
    requesterOrPublicSelected() {
      return (
        this.selectedDocumentVisibility &&
        (this.selectedDocumentVisibility.id === 'requester' ||
          this.selectedDocumentVisibility.id === 'public')
      )
    },
    selectedDocumentVisibility: {
      set(val) {
        this.setSelectedChangeDocVisibility(val)
      },

      get() {
        return this.selectedChangeDocVisibility
      }
    },

    changeVisibilityMessage: {
      set(val) {
        this.setMessageToRequester(val)
      },

      get() {
        return this.messageToRequester
      }
    },
    disableDeleteSubmitBtn() {
      return !(this.documentsSelectedCount || this.selectAll)
    },
    disableSubmitVisibilityBtn() {
      const msgPresentWithNoCheckbox =
        (this.selectedDocumentVisibility.id === 'public' ||
          this.selectedDocumentVisibility.id === 'requester') &&
        this.messageToRequester.length &&
        !this.sendMessageToRequester
      const checkboxCheckedWithNoMsg =
        (this.selectedDocumentVisibility.id === 'public' ||
          this.selectedDocumentVisibility.id === 'requester') &&
        !this.messageToRequester.length &&
        this.sendMessageToRequester
      if (
        msgPresentWithNoCheckbox ||
        checkboxCheckedWithNoMsg ||
        !this.documentsSelectedCount ||
        this.selectedDocumentVisibility === ''
      ) {
        return true
      }
      return false
    },
    disableSubmitExpirationBtn() {
      return this.selectedDocumentExpirationStatus == null
    },
    setDocVisibilityConfirmText() {
      return this.messageToRequester.length ? 'Save and send message' : 'Save'
    },
    invalidDocTitle() {
      return !this.docTitle.length
    },
    isDocEmptyModalActive() {
      return this.currentAction === 'docEmpty'
    }
  },
  methods: {
    ...mapActions('documents', ['createFolder']),

    ...mapActions('request_show', [
      'setSelectedChangeDocVisibility',
      'setMessageToRequester',
      'fetchDocumentsFolders',
      'fetchDataOptions',
      'setCurrentAction'
    ]),

    loadMoreMessageTemplates: throttle(function () {
      this.fetchDataOptions({
        searchText: this.dataFetchState.message_templates.search_text,
        dataType: 'message_templates'
      })
    }, 400),
    onDeleteSubmit() {
      this.$emit('on-doc-delete-submit')
      this.setCurrentAction('')
    },
    onDocRenameSubmit() {
      this.$emit('on-doc-rename-submit', this.docTitle)
      this.setCurrentAction('')
    },
    closeMoveToFolder() {
      this.setCurrentAction('')
      this.selectedFolder = null
    },

    async handleFolderAdd(val) {
      if (val) {
        // When options array is empty, val will be a string
        const label = isObject(val) ? val.label : val
        try {
          const { id: value } = await this.createFolder(label)
          this.selectedFolder = { label, value }
        } catch (_error) {
          this.selectedFolder = null
        }
      } else {
        this.selectedFolder = val
      }
    },

    handleTypingFolderSearch: throttle(function (val) {
      this.fetchDocumentsFolders({
        request_id: this.prettyId,
        search_text: val
      })
    }, 400),
    moveToFolder() {
      this.setCurrentAction('')

      this.$emit('on-move-to-folder-submit', {
        name: this.selectedFolder && this.selectedFolder.label,
        folder_id: this.selectedFolder && this.selectedFolder.value
      })

      this.selectedFolder = null
      this.searchFolderTerm = ''
    },
    searchMessageTemplates: throttle(function (searchText = '') {
      this.fetchDataOptions({ searchText, dataType: 'message_templates' })
    }, 400),
    clearVisibilityModalFields() {
      this.selectedMessageTemplates = []
      this.sendMessageToRequester = false
      this.changeVisibilityMessage = ''
      this.selectedDocumentVisibility = ''
      this.setMessageToRequester('')
      this.setCurrentAction('')
    },
    clearExpirationModalFields() {
      this.selectedDocumentExpirationStatus = null
      this.setCurrentAction('')
    },
    setMessageTemplate() {
      this.changeVisibilityMessage = this.selectedMessageTemplates
        .map((temp) => temp.description)
        .join('')
    },
    updateDocVisibility() {
      if (!this.disableSubmitVisibilityBtn) {
        this.$emit('on-visibility-submit', {
          selectedDocumentVisibility: this.selectedDocumentVisibility.id,
          messageToRequester: this.messageToRequester,
          messageTemplateIds: this.selectedMessageTemplates.map(
            (template) => template.value
          )
        })
        this.setCurrentAction('')
      } else {
        this.$toasted.error(
          'Please select a visibility and/or attach a message and select required checkbox'
        )
      }
    },
    updateDocExpirationStatus() {
      this.$emit('on-expiration-submit', {
        selectedDocumentExpirationStatus:
          this.selectedDocumentExpirationStatus.id
      })
      this.setCurrentAction('')
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/colors.scss';
.modal.visibility-form {
  max-height: 100vh;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;

  .modal-background {
    position: fixed;
  }
}
.doc-delete-modal {
  .nr-modal__header {
    background-color: $red;
    .nr-modal__title {
      color: $white;
    }
  }
  .nr-modal__footer {
    .modal__btn-primary {
      background-color: $red;
    }
  }
}

.nr-tooltip.inline {
  display: inline-block;
}

.inline-icon {
  display: inline-block;
  vertical-align: middle;
}
</style>
